/**
 * Session query- and mutation-key contracts.
 *
 * **These strings are frozen**, for two reasons — neither of which is
 * long-lived query caching. Session **query** entries are *not* persisted:
 * `PERSISTED_QUERY_KEYS` (`src/utils/reactQueryPersistConfig.ts`) allowlists
 * ten keys and none is session's, so they live in memory behind a 10-minute
 * `gcTime` and never touch localStorage. Session data is PHI; it staying out
 * of disk storage is the point.
 *
 * What does make a rename dangerous:
 *
 *   1. **Paused mutations persist.** `PersistQueryClientProvider` rehydrates
 *      `pending` mutations from localStorage across a deploy, which is why
 *      `clearStuckSessionCreationMutations` exists at all. Rename a creation
 *      mutation key and the clear-up matches nothing, so a hung POST from a
 *      previous build pins the session page on its skeleton forever.
 *   2. **Two copies during the migration.** Most keys are still declared both
 *      here and in the hook that owns them until its family moves (D series).
 *      Change one side and the app writes cache entries under a spelling that
 *      contracts readers never look up — no error, just a permanently cold
 *      read. `src/hooks/query/session-key-contract-parity.test.ts` is the
 *      interlock.
 *
 * Every value is therefore reproduced **verbatim** from the hook that used to
 * declare it, inconsistencies included: `'GetSessionById'` sits next to
 * `'GET_CONSULT_NOTES'` and next to two keys that are literal URL templates.
 * Normalising them is a deliberate, separately-shipped change with a
 * cache-buster note (OpenSpec `monorepo-session-data-core` task Z3) — never
 * part of a move.
 *
 * `keys.frozen.test.ts` asserts each value character-for-character; if a test
 * there fails, the fix is to restore the string, not to update the test.
 */

// ---------------------------------------------------------------------------
// Session by id (the hub)
// ---------------------------------------------------------------------------

export const GET_SESSION_BY_ID = 'GetSessionById';
export const GET_TEAM_MEMBER_SESSION_BY_ID = 'GetTeamMemberSessionById';

// ---------------------------------------------------------------------------
// Session lists
// ---------------------------------------------------------------------------

export const GET_SESSIONS = 'GetSessions';
export const GET_TEAM_MEMBER_SESSIONS = 'GetTeamMemberSessions';
export const GET_INFINITE_SESSIONS = 'GetInfiniteSessions';
export const GET_INFINITE_TEAM_MEMBER_SESSIONS = 'GetInfiniteTeamMemberSessions';
export const GET_INFINITE_SESSIONS_WITH_PAGINATION = 'GetInfiniteSessionsWithPagination';

/** The flag-gated session list (`useQuerySessionsList`). */
export const GET_SESSIONS_LIST = 'GetSessionsList';
/** Derived — kept as a template so the pair can never drift apart. */
export const GET_SESSIONS_LIST_FIRST_PAGE = `${GET_SESSIONS_LIST}_FirstPage`;

// The sidebar's status counts — the data half of the multiplayer↔scribe knot.
export const GET_SESSION_STATUS_COUNT = 'GetSessionStatusCount';
export const GET_TEAM_MEMBER_SESSION_STATUS_COUNT = 'GetTeamMemberSessionStatusCount';

/**
 * The All Sessions (`scope=all`) whole-scope status counts, backing the
 * status-filter dropdown's count badges. Deliberately carries no filter
 * params — see `SessionStatusCountsResponse` in `types.ts`.
 */
export const GET_SESSION_STATUS_COUNTS = 'GetSessionStatusCounts';

/**
 * Part of the v2 list KEY contract, not mere tuning: the infinite-sessions
 * cache key embeds the page size (`[GET_INFINITE_SESSIONS, 100]`), so the
 * version-dispatched cancel/invalidate commands must build keys with this
 * exact value. Moved from `src/constants/sessions.ts` (which re-exports).
 */
export const SESSIONS_PAGE_SIZE = 100;

/**
 * Elements 8 and 9 of the session-list cache key, so they are part of the KEY
 * contract for the same reason `SESSIONS_PAGE_SIZE` above is. Declared here
 * once: the app's filter atoms
 * (`src/components/layout/Navigation/sessions/header/toolbar/filters/atoms.ts`)
 * import them back as `DEFAULT_SORT_BY` / `DEFAULT_SORT_ORDER`, so a consumer
 * reproducing the default list key never has to reach an app atom module.
 */
export const SESSION_LIST_DEFAULT_SORT_BY = 'createdAt';
export const SESSION_LIST_DEFAULT_SORT_ORDER = 'desc';

// ---------------------------------------------------------------------------
// Transcript
// ---------------------------------------------------------------------------

export const GET_TRANSCRIPT_BY_SESSION_ID = 'GetTranscriptBySessionID';
export const GET_LIVE_TRANSCRIPT_BY_SESSION_ID = 'GetLiveTranscriptBySessionID';

// ---------------------------------------------------------------------------
// Consult notes
// ---------------------------------------------------------------------------

export const GET_CONSULT_NOTES = 'GET_CONSULT_NOTES';
export const GET_SESSION_CONSULT_NOTE_TIMELINE = 'GET_SESSION_CONSULT_NOTE_TIMELINE';

// ---------------------------------------------------------------------------
// Session activity log
// ---------------------------------------------------------------------------

export const GET_SESSION_ACTIVITY = 'GET_SESSION_ACTIVITY';

// ---------------------------------------------------------------------------
// Documents + context files
// ---------------------------------------------------------------------------

/**
 * Literal URL templates, NOT interpolated — the `${…}` and `{…}` are part of
 * the stored key string. Two different template dialects, both frozen.
 */
export const GET_DOCUMENT_BY_ID = '/sessions/${sessionId}/document/${documentId}';
export const GET_DEMO_DOCUMENT_BY_ID =
  '/demo/sessions/${sessionId}/document/${documentId}';
export const GET_DOCUMENTS_BY_ID = '/api/v1/ml-scribe/sessions/{session_id}/document';
export const GET_DEMO_DOCUMENTS_BY_ID =
  '/api/v1/ml-scribe/demo/sessions/{session_id}/document';

export const GET_SESSION_CONTEXT_FILES = 'get_session_context_files';
export const GET_SESSION_CONTEXT_FILES_PROGRESS = 'GET_SESSION_CONTEXT_FILES_PROGRESS';

// ---------------------------------------------------------------------------
// Session permissions (the FGA read used by useSessionPermissionCheck)
// ---------------------------------------------------------------------------

export const SESSION_PERMISSIONS_QUERY_KEY = 'sessionPermissions';

// ---------------------------------------------------------------------------
// Merge + feedback
// ---------------------------------------------------------------------------

export const GET_MERGEABLE_SESSIONS = 'GetMergeableSessions';
export const SESSION_FEEDBACK_QUERY_KEY = 'SESSION_FEEDBACK_QUERY_KEY';
export const VERIFY_LITE_QUERY_KEY = 'VERIFY_LITE_QUERY_KEY';
export const ENTITY_VERIFICATION_QUERY_KEY = 'entity_verification';
export const ENTITY_FEEDBACK_QUERY_KEY = 'entity_verification_feedback';
export const OVERALL_FEEDBACK_QUERY_KEY = 'entity_verification_overall_feedback';

// ---------------------------------------------------------------------------
// Mutation keys
// ---------------------------------------------------------------------------

export const CREATE_NEW_SESSION_MUTATION_KEY = 'CreateNewSessionV2';
export const CREATE_NEW_SESSION_FOR_OTHER_USER_MUTATION_KEY =
  'CreateNewSessionForOtherUser';

// ---------------------------------------------------------------------------
// Global-client seams
// ---------------------------------------------------------------------------

/**
 * The team-scoped session list belongs to the 403 → refetch-joined-teams
 * recovery in `reactQueryClient`: a 403 there means the reviewer lost team
 * access (APP-5396). Exported so the app's global client reads session's key
 * from session instead of embedding a literal.
 */
export const TEAM_MEMBERSHIP_GATED_SESSION_QUERY_KEY = GET_TEAM_MEMBER_SESSIONS;

/**
 * Session-creation mutation keys cleared after persistence rehydration — a
 * hung POST from a previous tab would otherwise pin the session page on its
 * skeleton forever. Order is preserved from the original literal.
 */
export const STUCK_SESSION_CREATION_MUTATION_KEYS: ReadonlyArray<readonly string[]> = [
  [CREATE_NEW_SESSION_MUTATION_KEY],
  [CREATE_NEW_SESSION_FOR_OTHER_USER_MUTATION_KEY],
];

// ---------------------------------------------------------------------------
// Key factories
// ---------------------------------------------------------------------------

/**
 * Narrowed team context for a member-scoped key. Construction sites must
 * resolve `teamId`/`memberId` to non-null strings before building, so
 * consumers don't re-check.
 */
export type SessionTeamContext = {
  teamId: string;
  memberId: string;
};

/**
 * Builders for the array shapes the hooks emit today. Each returns exactly
 * what the call site used to inline, so a cache entry written before a hook
 * moved is still addressable after it.
 */
export const sessionKeys = {
  /** `[GET_SESSION_BY_ID, { sessionId }]`, or the member-scoped 4-tuple. */
  byId: (sessionId: string, teamContext?: SessionTeamContext) =>
    teamContext
      ? [
          GET_TEAM_MEMBER_SESSION_BY_ID,
          { teamId: teamContext.teamId },
          { memberId: teamContext.memberId },
          { sessionId },
        ]
      : [GET_SESSION_BY_ID, { sessionId }],

  /** The infinite session list, keyed by page size. */
  infiniteList: (pageSize: number, teamContext?: SessionTeamContext) =>
    teamContext
      ? [
          GET_INFINITE_TEAM_MEMBER_SESSIONS,
          { teamId: teamContext.teamId },
          { memberId: teamContext.memberId },
          pageSize,
        ]
      : [GET_INFINITE_SESSIONS, pageSize],

  transcript: (sessionId: string) => [GET_TRANSCRIPT_BY_SESSION_ID, { sessionId }],
  liveTranscript: (sessionId: string) => [
    GET_LIVE_TRANSCRIPT_BY_SESSION_ID,
    { sessionId },
  ],
  consultNotes: (sessionId: string) => [GET_CONSULT_NOTES, { sessionId }],
  documents: (sessionId: string) => [GET_DOCUMENTS_BY_ID, { sessionId }],
  /** `[GET_DOCUMENT_BY_ID, { sessionId, documentId }]` — a single document. */
  documentById: (sessionId: string, documentId: string) => [
    GET_DOCUMENT_BY_ID,
    { sessionId, documentId },
  ],
  contextFiles: (sessionId: string) => [GET_SESSION_CONTEXT_FILES, { sessionId }],
} as const;

/**
 * Back-compat alias for the by-id builder that `sessionByIdKeys.ts` exported.
 * Kept so its existing importers keep compiling through the shim.
 */
export const buildLinkedSessionsQueryKey = (
  sessionId: string,
  teamContext?: SessionTeamContext
) => sessionKeys.byId(sessionId, teamContext);

/**
 * T11n (transcription-service) transcript cache root. Promoted from
 * `src/hooks/query/useQueryT11nTranscriptBySessionId.ts` (ARCFE-983) — a
 * cache identity the unlink mutation invalidates per member.
 */
export const TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY =
  'TRANSCRIPTION_SERVICE_TRANSCRIPTS' as const;

/** sessionStorage key: JSON array of session ids whose poor-audio warning was dismissed. */
export const POOR_AUDIO_WARNING_DISMISSED_SESSIONS_STORAGE_KEY =
  'poor-audio-warning-dismissed-sessions';

/**
 * Session-scoped chat list. Promoted from
 * `src/hooks/query/useQuerySessionChats.ts` (ARCFE-1003) — the page and
 * page-size arguments are part of the key, so the literal AND the tuple
 * shape are the cache identity.
 */
export const GET_SESSION_CHATS = 'get_session_chats';

/** Problem-based charting cache root (ARCFE-1003). */
export const GET_SESSION_PROBLEMS = 'GET_SESSION_PROBLEMS';

/** Context-document original-file blob cache (ARCFE-1004). */
export const GET_CONTEXT_DOCUMENT_FILE = 'get_context_document_file';

/**
 * The transcript-deleted SENTINEL (ARCFE-1004). Not a cache key — a WIRE
 * CONTRACT: generation endpoints reject with exactly this message when the
 * transcript is gone, and both the app guard and the package's failure
 * classifier compare against it. Single-site so the two cannot drift; the app
 * constant re-exports from here.
 */
export const TRANSCRIPT_DELETED_ERROR = 'TranscriptDeletedError';

// ---------------------------------------------------------------------------
// Session <-> patient-document links (ARCFE-1076, 2026-08-15)
// ---------------------------------------------------------------------------

/**
 * The link cluster's three frozen identities, moved here when the program
 * owner ruled the cluster SESSION's on the resource-owner rule — every route
 * is `/sessions/{id}/patient-documents`.
 *
 * `GET_SESSION_PATIENT_DOCUMENT_LINKS` moved VERBATIM out of
 * `@heidi/patients/contracts`, where ARCFE-1020 had frozen it under an
 * explicit "regardless of which lane ends up owning the hook". The string is
 * byte-identical; only the declaration site moved, so no cache partition
 * splits. The two MUTATION keys were declared inline in the app hooks
 * (`src/hooks/mutation/useMutate{,Un}Link*`) and are now single-site here.
 *
 * The QUERY key's identity is the LITERAL PLUS THE TUPLE: every reader and
 * both writers build `[GET_SESSION_PATIENT_DOCUMENT_LINKS, { sessionId }]` —
 * an object element, not a bare string — so `sessionPatientDocumentLinkKeys`
 * below is the single builder and `keys.frozen.test.ts` pins the emitted
 * array shape, not just the string.
 */
export const GET_SESSION_PATIENT_DOCUMENT_LINKS = 'get_session_patient_document_links';
export const LINK_SESSION_PATIENT_DOCUMENTS = 'link_session_patient_documents';
export const UNLINK_SESSION_PATIENT_DOCUMENT = 'unlink_session_patient_document';

/** Tuple builders for the link cluster — the shape is the cache identity. */
export const sessionPatientDocumentLinkKeys = {
  /** `[GET_SESSION_PATIENT_DOCUMENT_LINKS, { sessionId }]` — the link list. */
  list: (sessionId: string | null | undefined) => [
    GET_SESSION_PATIENT_DOCUMENT_LINKS,
    { sessionId },
  ],
  /** `[LINK_SESSION_PATIENT_DOCUMENTS, { sessionId }]` — the link mutation. */
  link: (sessionId: string) => [LINK_SESSION_PATIENT_DOCUMENTS, { sessionId }],
  /** `[UNLINK_SESSION_PATIENT_DOCUMENT, { sessionId }]` — the unlink mutation. */
  unlink: (sessionId: string) => [UNLINK_SESSION_PATIENT_DOCUMENT, { sessionId }],
} as const;

// ---------------------------------------------------------------------------
// The session-header modal cones' data identities (2026-08-28)
// ---------------------------------------------------------------------------

/**
 * The supported-language vocabulary read (`GET /language/supported-languages`),
 * moved verbatim from `src/hooks/query/useQuerySupportedLanguages.ts`.
 *
 * The endpoint is global rather than session-scoped, and the declaration is
 * here because the two `ui--session-header` cones that block on it name this
 * door by name — "carve `useQuerySupportedLanguages` behind
 * `@heidi/session/data`". The api function's `@domain REVIEW` tag resolves to
 * session with the carve.
 */
export const GET_SUPPORTED_LANGUAGES = 'GetSupportedLanguages';

/**
 * The session input/output language write. Moved verbatim from
 * `src/hooks/mutation/useMutateUpdateSessionLanguage.ts`, where it was a
 * module-private `const` (not exported, so it carried no cache-key baseline
 * row of its own until the same file's row is dropped by this move).
 */
export const UPDATE_SESSION_LANGUAGE = 'UpdateSessionLanguage';

/** Document content save. Literal unchanged from the app hook that declared it. */
export const UPDATE_DOCUMENT_BY_ID = 'UPDATE_DOCUMENT_BY_ID';

/** Find-and-replace across a session's documents. */
export const UPDATE_ALL_DOCUMENTS_FIND_REPLACE = 'UPDATE_ALL_DOCUMENTS_FIND_REPLACE';

/** Find-and-replace written onto the session itself. */
export const UPDATE_SESSION_FIND_REPLACE = 'UpdateSessionFindReplace';

/** Personalized export-CTA copy for one session document. */
export const DOCUMENT_EXPORT_SUGGESTIONS_QUERY_KEY = 'document-export-suggestions';

/** Shadow Heidi clinician research note. The string is the stored identity. */
export const UPDATE_RESEARCH_CLINICIAN_NOTE = 'updateResearchClinicianNote';

/** Shadow Heidi research metadata on a session. */
export const UPDATE_SESSION_RESEARCH_METADATA = 'updateSessionResearchMetadata';

/**
 * The session date/time write pair. Both moved verbatim from
 * `src/hooks/mutation/useMutateUpdateSessionDateTime.ts`; the SCREAMING_SNAKE
 * spelling is the stored identity, not a style slip — see this file's header.
 */
export const UPDATE_SESSION_DATE_TIME = 'UPDATE_SESSION_DATE_TIME';
export const UPDATE_OTHER_USER_SESSION_DATE_TIME = 'UPDATE_OTHER_USER_SESSION_DATE_TIME';

/**
 * The team-member session write.
 *
 * This one was an INLINE LITERAL — `mutationKey: ['UpdateOtherUserSession']` at
 * `src/hooks/mutation/useMutateUpdateOtherUserSession.ts:35` — so naming it here
 * is a relocation of an existing identity, not a new key. The string is
 * byte-identical; nothing re-partitions.
 */
export const UPDATE_OTHER_USER_SESSION = 'UpdateOtherUserSession';

/**
 * The context autosave write. The literal is the stored identity.
 */
export const UPDATE_CONTEXT_BY_SESSION_ID = 'UPDATE_CONTEXT_BY_SESSION_ID';

// Session writes moved with their hooks. The literals are the stored identities.
export const GRADUATE_ADAPTIVE_LEARNING = 'graduateAdaptiveLearning';
export const UPDATE_SESSION_RECORDING_STATUS = 'UpdateSessionRecordingStatus';
export const UPDATE_SESSION_REVIEW_CONFIRMED = 'UpdateSessionReviewConfirmed';

/**
 * Session-field writes. Each literal moved verbatim from the app hook that
 * declared it; renaming one partitions a live mutation cache.
 */
export const UPDATE_SESSION_IS_TAB_SHARING = 'UpdateSessionIsTabSharing';
export const UPDATE_SESSION_GIST = 'UpdateSessionGist';
export const UPDATE_SESSION_CONTEXT_ONLY = 'UpdateSessionContextOnly';
export const UPDATE_SESSION_DURATION = 'updateSessionDuration';
export const UPDATE_LINKED_SESSIONS = 'UpdateLinkedSessions';
export const DOCUMENT_GENERATE_INSIGHTS_MUTATION = 'documentGenerateInsights';

/**
 * The linked-sessions-by-patient-profile pair, moved verbatim from
 * `src/hooks/query/useQueryLinkedSessionsByProfileId.ts`.
 *
 * The team-member key's identity is the LITERAL PLUS THE TUPLE — every reader
 * and the invalidation command build
 * `[GET_TEAM_MEMBER_SESSIONS_BY_PROFILE_ID, patientProfileId, teamId, memberId]`
 * as four POSITIONAL elements, not an object element like the by-id keys above.
 * `linkedSessionsByProfileKeys` below is the single builder and
 * `keys.frozen.test.ts` pins the emitted arrays, not just the strings.
 */
export const GET_LINKED_SESSIONS_BY_PROFILE_ID = 'getLinkedSessionsByProfileId';
export const GET_TEAM_MEMBER_SESSIONS_BY_PROFILE_ID = 'getTeamMemberSessionsByProfileId';

/**
 * Tuple builders for the linked-sessions pair — the shape is the cache
 * identity, and the two arms are two different caches rather than one keyed
 * cache, which is why the reader and the invalidator must agree arm for arm.
 *
 * `teamId` / `memberId` are passed through UNCOERCED (`string | undefined`).
 * The reader registers its key before the team details resolve, and widening
 * an unresolved id to `''` would emit a third spelling that neither the loaded
 * reader nor the invalidator ever addresses.
 */
export const linkedSessionsByProfileKeys = {
  /** `[GET_LINKED_SESSIONS_BY_PROFILE_ID, patientProfileId]` — own sessions. */
  own: (patientProfileId: string) => [
    GET_LINKED_SESSIONS_BY_PROFILE_ID,
    patientProfileId,
  ],
  /** The four-element team-member arm. */
  teamMember: (
    patientProfileId: string,
    teamId: string | undefined,
    memberId: string | undefined
  ) => [GET_TEAM_MEMBER_SESSIONS_BY_PROFILE_ID, patientProfileId, teamId, memberId],
} as const;

/**
 * The foreign-session ownership scan behind the patient-delete guard
 * (COLAB-394). A NEW identity declared here at birth: `src/**` is
 * declaration-locked and this is a session read, so the session door owns it.
 */
export const GET_FOREIGN_SESSION_OWNERS = 'getForeignSessionOwners';

/**
 * The identity is the LITERAL PLUS THE TUPLE, and the profile ids are SORTED
 * before they enter it. Callers pass a selection, and a selection has no
 * inherent order — a user who ticks A then B and a user who ticks B then A are
 * asking the same question, so an unsorted key would mint two cache entries
 * for one answer and fetch twice. `keys.frozen.test.ts` pins the emitted array.
 */
export const foreignSessionOwnersKeys = {
  forProfiles: (patientProfileIds: readonly string[]) => [
    GET_FOREIGN_SESSION_OWNERS,
    [...patientProfileIds].sort(),
  ],
} as const;

// ---------------------------------------------------------------------------
// XML form documents
// ---------------------------------------------------------------------------

/**
 * The filled-XML field read's cache root. A NEW identity, declared here at
 * birth rather than in the app — `src/**` is declaration-locked, and the
 * session package is the domain door this resource belongs to.
 *
 * The identity is the LITERAL PLUS THE TUPLE: readers build
 * `[GET_XML_DOCUMENT_FIELDS, { documentId }]`, so `xmlDocumentFieldsKey` is
 * the single builder and `keys.frozen.test.ts` pins the emitted array.
 */
export const GET_XML_DOCUMENT_FIELDS = 'GetXmlDocumentFields';

/**
 * `[GET_XML_DOCUMENT_FIELDS, { documentId }]` — the filled-XML field list.
 *
 * An XFA PDF document adds `format: 'xfa_pdf'` to the same object, so the XML
 * identity is unchanged and the `[GET_XML_DOCUMENT_FIELDS]` prefix still
 * invalidates both.
 */
export const xmlDocumentFieldsKey = (
  documentId: string | null | undefined,
  format: 'xml' | 'xfa_pdf' = 'xml'
) => [
  GET_XML_DOCUMENT_FIELDS,
  format === 'xml' ? { documentId } : { documentId, format },
];

// The session-window family was addressed by this string. It is not a server
// cache key, and the literal stays byte-identical for the one writer that
// still builds a tuple from it.
export const GET_SESSION_WINDOWS = 'GetSessionWindows';
